import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import { useReservations } from '../hooks/useReservations';
import { ReservationResponseDto } from '../types/reservation';
import { equipmentService } from '../services/equipmentService';
import { getImageUrl } from '../utils/imageUrl';
import { formatCurrency } from '../utils/helpers';
import { ImageEquipment } from '../types/equipment';
import { useReservationActions } from '@hooks/useReservationActions';
import { ReservationStatus } from '@/types';
import RESERVATION_STATUS_OPTIONS, { STATUS_VISUALS } from '@constants/reservationStatus';
import { Icon } from '@icons/registry';

const BookingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past' | 'cancelled'>('upcoming');
  const navigate = useNavigate();

  const {
    data: reservations,
    loading,
    setStatusFilter,
  } = useReservations();

  const {
  cancelReservation,
  markInUseIssue,
  } = useReservationActions();

  // Actualizar filtro cuando cambie la pestaña
  React.useEffect(() => {
    const statusMap: Record<string, string | undefined> = {
      upcoming: '',
      past: 'completed',
      cancelled: 'cancelled',
    };
    const status = statusMap[activeTab];
    setStatusFilter(status);
  }, [activeTab, setStatusFilter]);

  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('es-ES', options);
  };

  const findStatusOption = (status: string) => RESERVATION_STATUS_OPTIONS.find((s) => s.value === status);

  const BookingCard = ({ booking }: { booking: ReservationResponseDto }) => {
    const [expanded, setExpanded] = useState(false);
  const [equipmentName, setEquipmentName] = useState<string | null>(null);
  const [equipmentImage, setEquipmentImage] = useState<string | null>(null);

    useEffect(() => {
      let mounted = true;
      (async () => {
        try {
          const eq = await equipmentService.getEquipmentById(booking.equipmentId);
          if (!mounted) return;
          if (eq) {
            setEquipmentName(eq.name);
            const imgs = (eq.images || []);
            const first = imgs[0];
            if (first && typeof first === 'object' && 'url' in first) {
              const img = first as ImageEquipment;
              setEquipmentImage(getImageUrl(img.url));
            }
          } else {
            setEquipmentName(`Equipo ${booking.equipmentId}`);
          }
        } catch (err) {
          console.error('Error fetching equipment', err);
          setEquipmentName(`Equipo ${booking.equipmentId}`);
        }
      })();
      return () => { mounted = false; };
    }, [booking.equipmentId]);

    return (
      <div className="overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="p-4 sm:p-6">
          <div className="justify-between sm:flex">
            <div className="flex-1">
              <div className="flex flex-col items-start mb-4 sm:items-center sm:flex-row">
                <img 
                  src={equipmentImage || '/defaultequipment.png'} 
                  alt={equipmentName || booking.equipmentId}
                  className="object-cover w-full h-24 mb-4 rounded-lg sm:w-24 sm:h-24 sm:mb-0 sm:mr-4"
                />
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3">
                    <h3 className="text-lg font-semibold text-gray-900">{equipmentName || booking.reservationCode}</h3>
                    {(() => {
                      const opt = findStatusOption(booking.status);
                      const visual = STATUS_VISUALS[booking.status] || { bg: 'bg-gray-100', text: 'text-gray-800', icon: 'default' };
                      const label = opt ? opt.label : booking.status;
                      return (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${visual.bg} ${visual.text}`}>
                          <Icon name={visual.icon} size="sm" className={`mr-1 ${visual.text}`} />
                          {label}
                        </span>
                      );
                    })()}
                  </div>
                  <div className="flex items-center mt-1 text-sm text-gray-500">
                    <Calendar size={14} className="mr-1" />
                    <span>{formatDate(booking.startDate)} - {formatDate(booking.endDate)}</span>
                  </div>
                </div>
              </div>
              
              <div className="pt-4 mt-4 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm text-gray-500">Coste total</span>
                    <p className="font-bold text-gray-900">{formatCurrency(booking.totalPrice)}</p>
                  </div>
                  
                  <button
                    onClick={() => setExpanded(!expanded)}
                    className="flex items-center text-sm font-medium text-blue-600 cursor-pointer"
                  >
                    {expanded ? (
                      <>
                        <span>Mostrar menos</span>
                        <ChevronUp size={16} className="ml-1" />
                      </>
                    ) : (
                      <>
                        <span>Ver detalles</span>
                        <ChevronDown size={16} className="ml-1" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          {expanded && (
            <div className="pt-6 mt-6 border-t border-gray-100 fade-in">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <h4 className="mb-3 text-sm font-medium text-gray-900">Detalles de la reserva</h4>
                  <div className="p-4 rounded-lg bg-gray-50">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-gray-500">Código de reserva</p>
                        <p className="font-medium">{booking.reservationCode}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Estado</p>
                        <p className="font-medium capitalize">{(findStatusOption(booking.status)?.label) ?? booking.status}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-gray-500">Periodo de alquiler</p>
                        <p className="font-medium">{formatDate(booking.startDate)} - {formatDate(booking.endDate)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-3 mt-6">
                {/* Cancelar permitido para Confirmed, Pending Payment y Payment Failed */}
                {[
                  ReservationStatus.CONFIRMED,
                  ReservationStatus.PENDING_PAYMENT,
                  ReservationStatus.PAYMENT_FAILED,
                ].includes(booking.status as ReservationStatus) && (
                  <>
                    <button className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                      Modificar reserva
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          await cancelReservation(booking.id);
                        } catch (err) {
                          console.error('Error al cancelar la reserva', err);
                          alert('Error al cancelar la reserva');
                        }
                      }}
                      className="px-4 py-2 text-red-600 bg-white border border-red-300 rounded-lg hover:bg-red-50"
                    >
                      Cancelar reserva
                    </button>
                  </>
                )}

                {/* Marcar incidencia en uso cuando está Active */}
                {booking.status === ReservationStatus.ACTIVE && (
                  <button
                    onClick={async () => {
                      try {
                        await markInUseIssue(booking.id);
                      } catch (err) {
                        console.error('Error al marcar incidencia en uso', err);
                        alert('Error al marcar incidencia en uso');
                      }
                    }}
                    className="px-4 py-2 bg-white border rounded-lg cursor-pointer text-amber-700 border-amber-300 hover:bg-amber-50"
                  >
                    Marcar incidencia en uso
                  </button>
                )}

                {booking.status === ReservationStatus.COMPLETED && (
                  <button className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                    Dejar una reseña
                  </button>
                )}
                
                <button 
                  onClick={() => navigate(`/equipment/${booking.equipmentId}`)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Ver equipo
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container px-4 py-8 mx-auto">
        <h1 className="mb-6 text-2xl font-bold text-gray-800">Mis reservas</h1>
        
        {/* Navegación de pestañas */}
        <div className="flex mb-6 border-b border-gray-200">
          <button
            className={`py-2 px-4 text-center border-b-2 font-medium text-sm ${
              activeTab === 'upcoming'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            onClick={() => setActiveTab('upcoming')}
          >
            Próximas
          </button>
          <button
            className={`py-2 px-4 text-center border-b-2 font-medium text-sm ${
              activeTab === 'past'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            onClick={() => setActiveTab('past')}
          >
            Pasadas
          </button>
          <button
            className={`py-2 px-4 text-center border-b-2 font-medium text-sm ${
              activeTab === 'cancelled'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            onClick={() => setActiveTab('cancelled')}
          >
            Canceladas
          </button>
        </div>
        
        {/* Tarjetas de reserva */}
        <div className="space-y-6">
          {loading && (
            <div className="py-12 text-center">Cargando reservas...</div>
          )}

          {!loading && reservations.length === 0 && (
            <div className="p-8 text-center bg-white rounded-lg shadow-md">
              <div className="inline-flex p-3 mb-4 bg-blue-100 rounded-full">
                <Calendar size={24} className="text-blue-600" />
              </div>
              <h2 className="mb-2 text-xl font-semibold text-gray-800">No hay reservas</h2>
              <p className="mb-6 text-gray-600">
                No tienes reservas en esta categoría.
              </p>
              <button 
                onClick={() => navigate('/search')}
                className="px-5 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Buscar equipo
              </button>
            </div>
          )}

          {!loading && reservations.map((r) => (
            <BookingCard key={r.id} booking={r} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default BookingsPage;